import { safeMedicalCertificateFileName } from "../../../utils/medicalCertificateFileName";
import React, { useEffect, useRef, useState } from "react";
import { uploadBytes, getDownloadURL, deleteObject, ref } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { notifyError, notifyWarning, notifyTopRight } from "../toast/SetToast";
import { canUploadAnnualMaskCertificate, isAnnualMaskCertificateUploadLocked, saveAnnualMaskSignedCertificate } from "./annualMaskPhysicianUpload";
import { readAnnualMaskSignedPressure } from "./annualMaskClinicalPdfReader";

export default function AnnualMaskCertificateUploadButton({ caseItem, currentUser, onUploaded }) {
  const inputRef = useRef(null);
  const busyRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const scope = `${currentUser && currentUser.uid}|${currentUser && currentUser.email}|${currentUser && currentUser.displayName}|${JSON.stringify(currentUser && currentUser.roles)}|${caseItem.refPath}|${caseItem.hn}`;
  const activeScope = useRef(scope);
  activeScope.current = scope;
  useEffect(() => () => { activeScope.current = null; }, []);
  const allowed = canUploadAnnualMaskCertificate(caseItem, currentUser);
  const locked = isAnnualMaskCertificateUploadLocked(caseItem);

  const upload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || busyRef.current) return;
    if (!allowed || locked) return;
    if (!caseItem.hn || !caseItem.id || !caseItem.batchId || !caseItem.refPath) {
      notifyError("Reload the patient before uploading this certificate.");
      return;
    }
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      notifyWarning("Upload the signed medical certificate as a PDF file.");
      return;
    }
    busyRef.current = true;
    setSaving(true);
    let uploadedRef;
    let saved = false;
    try {
      const pressureReview = await readAnnualMaskSignedPressure(file, caseItem.certificateData);
      const savedFileName = `${Date.now()}_${safeMedicalCertificateFileName(file.name)}`;
      const storagePath = `annual-mask-purchases/${caseItem.hn}/${caseItem.batchId}/${caseItem.id}/signed-medical-certificate/${savedFileName}`;
      if (activeScope.current !== scope) throw new Error("Account or patient changed. Open the case again before uploading.");
      uploadedRef = ref(storage, storagePath);
      await uploadBytes(uploadedRef, file, { contentType: "application/pdf" });
      const url = await getDownloadURL(uploadedRef);
      if (activeScope.current !== scope) throw new Error("Account or patient changed. Open the case again before uploading.");
      const certificate = {
        pressureReview, fileName: file.name, savedFileName, storagePath, url,
        contentType: "application/pdf", uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser.displayName || currentUser.email || "",
      };
      const previous = await saveAnnualMaskSignedCertificate({ database: db, caseItem, certificate, currentUser });
      saved = true;
      if (previous && previous.storagePath && previous.storagePath !== storagePath) {
        try {
          await deleteObject(ref(storage, previous.storagePath));
        } catch (error) {
          console.warn("Unable to remove replaced signed medical certificate", error);
        }
      }
      if (activeScope.current === scope) {
        onUploaded(caseItem, certificate);
        notifyTopRight("Signed medical certificate uploaded.");
      }
    } catch (error) {
      if (uploadedRef && !saved) {
        try { await deleteObject(uploadedRef); }
        catch (cleanupError) { console.warn("Unable to clean up signed medical certificate", cleanupError); }
      }
      notifyError(error.message || "Cannot upload signed medical certificate.");
    } finally {
      busyRef.current = false;
      if (activeScope.current !== null) setSaving(false);
    }
  };

  if (!allowed) return null;
  return (
    <div className="mt-2">
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="d-none" aria-label="Signed medical certificate PDF" onChange={upload} />
      <button type="button" className="btn btn-outline-primary btn-sm" disabled={saving || locked}
        title={locked ? "Certificate changes are locked because the case has advanced." : "Upload signed medical certificate as PDF"}
        onClick={() => inputRef.current.click()}>
        {saving ? "Uploading..." : caseItem.signedMedicalCertificate ? "Replace signed certificate" : "Upload signed certificate"}
      </button>
    </div>
  );
}
